import React, {PropsWithChildren, useEffect, useMemo, useRef, useState} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {ReportsPage_viewer} from './__generated__/ReportsPage_viewer.graphql';
import {RouteComponentProps} from 'react-router-dom';
import Styled from 'styled-components';
import {Button, FlexColumn, FlexRow, Heading, SearchField, Tabs} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import CustomScrollDiv from '../../shared/components/scroll-bars/custom_scroll_div';
import {ReportRow} from './ReportTypes';
import {getNotNullishNodes} from '../../shared/util/NotNullPredicate';
import Util from '../../shared/util/util';
import {trackEvent} from '../../../tracking/amplitude/TrackingV2';
import ReportsPageChangeViewButton from './ReportsPageChangeViewButton';
import {mapReportToRow} from './ReportRowMapper';
import ReportsTabContent from './ReportsTabContent';
import {hasPermission} from '../../shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import {createReport} from './actions/create-report/CreateReport';
import {useTrackPage} from '../../../tracking/amplitude/hooks/useTrackPage';
import {useLocalStorage} from 'react-use';

export type UseHistoryType = RouteComponentProps['history'];

const GROUP_BY_LOCALSTORAGE_KEY = 'report-page-group-by';

const Toolbar = ({children}: PropsWithChildren<unknown>) => <FlexRow>{children}</FlexRow>;
Toolbar.Left = ({children}: PropsWithChildren<unknown>) => <FlexRow gap={'s'}>{children}</FlexRow>;
Toolbar.Right = ({children}: PropsWithChildren<unknown>) => (
	<FlexRow gap={'s'} justifyContent={'end'}>
		{children}
	</FlexRow>
);

const PageWrapper = Styled.div`
  padding: 32px;
`;

interface ReportsPageProps {
	viewer: ReportsPage_viewer;
}

const ReportsPageTabs = {
	private: 'private',
	shared: 'shared',
	all: 'all',
};

const ReportsPage = ({viewer}: ReportsPageProps): React.JSX.Element => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const scrollRef = useRef(null);
	const [search, setSearch] = useState('');

	const [tab, setTab] = useLocalStorage<string>('ReportsPage.SelectedTab', ReportsPageTabs.shared);
	const selectedTab = tab ?? ReportsPageTabs.shared;

	const [groupings, setGroupings] = useState<string[]>(() =>
		JSON.parse(Util.localStorageGetItem(GROUP_BY_LOCALSTORAGE_KEY) || JSON.stringify([]))
	);

	useEffect(() => {
		document.title = `Reports - Forecast`;
	}, []);

	useTrackPage('Reports', {tabName: tab});

	const groupingColumnHeader = groupings.length
		? formatMessage({id: 'report.grouping'}) +
		  ': ' +
		  groupings.map(grouping => formatMessage({id: 'report.column.' + grouping})).join(' / ')
		: 'Name';

	const onGroupingsChange = (groupings: string[]) => {
		Util.localStorageSetItem(GROUP_BY_LOCALSTORAGE_KEY, JSON.stringify(groupings));
		setGroupings(groupings);

		const groupingObj = groupings.reduce((result, item, index) => {
			result[`level_${index + 1}`] = item;
			return result;
		}, {});

		trackEvent('Standard Report', 'Grouping Changed', {
			groupBy: groupings,
			...groupingObj,
		});
	};

	const allReports: ReportRow[] = useMemo(() => {
		return getNotNullishNodes(viewer?.company?.allSavedReports?.edges)
			.filter(report => !report.converted)
			.map(report => mapReportToRow(report, viewer.actualPersonId));
	}, [viewer?.company?.allSavedReports]);

	const privateReports: ReportRow[] = useMemo(
		() => allReports.filter(report => report.isReportOwner && report.privateAccess),
		[allReports]
	);
	const companyReports: ReportRow[] = useMemo(() => allReports.filter(report => !report.privateAccess), [allReports]);

	const isAdmin = hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS);

	return (
		<CustomScrollDiv
			setScrollbarsRef={node => {
				scrollRef.current = node;
			}}
		>
			<PageWrapper>
				<FlexColumn gap={'l'}>
					<Toolbar>
						<Toolbar.Left>
							<Heading size={'3'}>
								<FormattedMessage id="header.reports" />
							</Heading>
						</Toolbar.Left>
						<Toolbar.Right>
							<SearchField value={search} onChange={setSearch} data-cy={'reports-search'} />
							<ReportsPageChangeViewButton groupings={groupings} onGroupingsChange={onGroupingsChange} />
							<Button data-cy={'create-reports-button'} onPress={createReport}>
								<FormattedMessage id="reports.create-report" />
							</Button>
						</Toolbar.Right>
					</Toolbar>
					<Tabs value={selectedTab} onValueChange={setTab}>
						<Tabs.List>
							<Tabs.Tab
								data-cy={'my-reports-tab'}
								value={ReportsPageTabs.private}
							>{`Private (${privateReports.length})`}</Tabs.Tab>
							<Tabs.Tab
								data-cy={'company-reports-tab'}
								value={ReportsPageTabs.shared}
							>{`Shared (${companyReports.length})`}</Tabs.Tab>
							{isAdmin && (
								<Tabs.Tab
									data-cy={'all-reports-tab'}
									value={ReportsPageTabs.all}
								>{`All (${allReports.length})`}</Tabs.Tab>
							)}
						</Tabs.List>
						<Tabs.Content value={ReportsPageTabs.private}>
							<ReportsTabContent
								enableSelection={true}
								company={viewer.company}
								reports={privateReports}
								search={search}
								groupings={groupings}
								scrollRef={scrollRef}
								groupingColumnHeader={groupingColumnHeader}
							/>
						</Tabs.Content>
						<Tabs.Content value={ReportsPageTabs.shared}>
							<ReportsTabContent
								enableSelection={isAdmin}
								company={viewer.company}
								reports={companyReports}
								search={search}
								groupings={groupings}
								scrollRef={scrollRef}
								groupingColumnHeader={groupingColumnHeader}
							/>
						</Tabs.Content>
						{isAdmin && (
							<Tabs.Content value={ReportsPageTabs.all}>
								<ReportsTabContent
									enableSelection={true}
									company={viewer.company}
									reports={allReports}
									search={search}
									groupings={groupings}
									scrollRef={scrollRef}
									groupingColumnHeader={groupingColumnHeader}
								/>
							</Tabs.Content>
						)}
					</Tabs>
				</FlexColumn>
			</PageWrapper>
		</CustomScrollDiv>
	);
};

export default createFragmentContainer(ReportsPage, {
	viewer: graphql`
		fragment ReportsPage_viewer on Viewer {
			id
			actualPersonId
			availableFeatureFlags {
				key
			}
			company {
				...ReportsTable_company
				id
				modules {
					moduleType
				}
				allSavedReports(first: 100000, state: "SAVED") @connection(key: "Company_allSavedReports", filters: []) {
					edges {
						node {
							id
							name
							type
							person {
								id
								fullName
								email
								profilePictureId
							}
							lastViewed
							lastViewedName
							lastViewedBy {
								id
								fullName
								email
								profilePictureId
							}
							converted
							reportService
							shares(first: 100) @connection(key: "Report_shares", filters: []) {
								edges {
									node {
										id
										user {
											id
											email
										}
									}
								}
							}
							updatedAt
							privateAccess
							state
							favorite
						}
					}
				}
			}
		}
	`,
});
